import React, { useMemo } from "react";
import { Treemap, ResponsiveContainer, Tooltip } from "recharts";
import {
  SERIES_COLORS,
  PALETTE,
  fmtMoney,
  fmtDays,
  daysFromMoney,
} from "@/lib/chart-utils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

/**
 * BudgetTreemap — proportional rectangles of budget sectors,
 * sized by "days of national life invested" (= amount / salaire_journalier_moyen).
 *
 * Props:
 *   aggregatedBudgetLines : [{sector, amount, percentage}]
 *   salaireJournalierMoyen: number (for conversion)
 *   currency              : string
 */
export default function BudgetTreemap({
  aggregatedBudgetLines = [],
  salaireJournalierMoyen,
  currency = "UM",
}) {
  const data = useMemo(() => {
    if (!aggregatedBudgetLines?.length) return [];
    return aggregatedBudgetLines.map((bl, i) => {
      const amount = Number(bl.amount) || 0;
      const days = daysFromMoney(amount, salaireJournalierMoyen) || 0;
      return {
        name: bl.sector,
        amount,
        days,
        percentage: bl.percentage,
        size: days > 0 ? days : amount, // prefer days for sizing if available
        color: SERIES_COLORS[i % SERIES_COLORS.length],
      };
    });
  }, [aggregatedBudgetLines, salaireJournalierMoyen]);

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.[0]) return null;
    const p = payload[0].payload;
    return (
      <div
        className="rounded-md border border-border bg-card p-3 text-xs shadow-lg"
        data-testid="tooltip-treemap"
      >
        <div className="font-semibold" style={{ color: p.color }}>
          {p.name}
        </div>
        <div className="mt-1 flex justify-between gap-4">
          <span className="text-muted-foreground">Montant</span>
          <span className="font-mono">{fmtMoney(p.amount, currency)}</span>
        </div>
        {p.percentage != null && (
          <div className="flex justify-between gap-4">
            <span className="text-muted-foreground">% du budget</span>
            <span className="font-mono">{Number(p.percentage).toFixed(1)} %</span>
          </div>
        )}
        <div className="flex justify-between gap-4 border-t border-border pt-1 mt-1">
          <span className="text-muted-foreground">Vies investies</span>
          <span className="font-mono font-bold">{fmtDays(p.days)}</span>
        </div>
      </div>
    );
  };

  // Custom content to render sector label + days inside the block when large enough
  const Content = (props) => {
    const { x, y, width, height, name, days, color } = props;
    if (width < 60 || height < 30) {
      return (
        <g>
          <rect
            x={x}
            y={y}
            width={width}
            height={height}
            fill={color}
            stroke="var(--background)"
          />
        </g>
      );
    }
    return (
      <g>
        <rect
          x={x}
          y={y}
          width={width}
          height={height}
          fill={color}
          stroke="var(--background)"
        />
        <text
          x={x + 8}
          y={y + 18}
          fill="#fff"
          fontSize={12}
          fontWeight={600}
          style={{ pointerEvents: "none" }}
        >
          {name}
        </text>
        <text
          x={x + 8}
          y={y + 34}
          fill="#fff"
          fontSize={11}
          opacity={0.9}
          style={{ pointerEvents: "none" }}
        >
          {days > 1000 ? fmtDays(days) : fmtDays(days)}
        </text>
      </g>
    );
  };

  if (data.length === 0) {
    return (
      <Card data-testid="chart-treemap-empty">
        <CardHeader>
          <CardTitle className="text-base">Budget national en vies humaines</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          Aucune ligne budgétaire sectorielle disponible. Uploadez une loi de
          finances ou un budget pour voir la ventilation.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="chart-treemap">
      <CardHeader>
        <CardTitle className="flex items-center justify-between text-base">
          <span>Budget national en vies humaines</span>
          <span className="text-xs font-normal text-muted-foreground">
            {data.length} secteur{data.length > 1 ? "s" : ""}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-80 w-full" data-testid="chart-treemap-container">
          <ResponsiveContainer width="100%" height="100%">
            <Treemap
              data={data}
              dataKey="size"
              nameKey="name"
              stroke={PALETTE.blue}
              content={<Content />}
              isAnimationActive={true}
            >
              <Tooltip content={<CustomTooltip />} />
            </Treemap>
          </ResponsiveContainer>
        </div>
        <div className="mt-2 border-t border-border pt-2 text-xs text-muted-foreground">
          La taille de chaque bloc est proportionnelle au nombre de{" "}
          <strong>jours de vie investis</strong> (montant ÷ salaire journalier
          moyen).
        </div>
      </CardContent>
    </Card>
  );
}
